<template>
    <div class="flex-none flex flex-col relative overflow-y-auto">
        <div
            ref="stageHeader"
            class="flex items-center min-h-66 px-16 py-24"
        >
            <progress-icon :stage="stage" class="mr-8"></progress-icon>

            <span class="font-semibold uppercase mr-4 line-clamp-1" :title="stage.name">{{ stage.name }}</span>
            <span class="text-primary-80 mr-auto">({{ applicationCount }})</span>

            <div v-show="stageApplications.length" :style="{ marginRight: scrollPadding + 'px' }" class="pl-8">
                 <checkbox
                     @change="toggleSelectedApplications"
                     :checked="hasSelectedItems"
                     :indeterminate="hasSelectedItems && hasUnselectedItems"
                 />
            </div>
        </div>

        <loading v-if="fetching"/>

        <div ref="stageContainer"
             :class="[ stageApplications.length ? 'overflow-y-auto' : '' ]"
             class="relative grow flex-1 flex bg-gradient-to-b from-primary/5 rounded"
        >
            <div :id="'stage' + stage.name"
                 class="min-h-full relative grow p-12"
                 @dragenter="handleDragEnter"
                 @dragleave="handleDragLeave"
                 @dragover.prevent
                 @drop.prevent="handleDrop">
                <div :class="[ canDrop ? 'pointer-events-none' : '' ]" class="flex flex-col">

                    <div v-for="application in stageApplications"
                         :key="'stage_application-' + application.id"
                         :class="application.referrer_source === 'interim' ? selection.includes(application.id) ? 'border-secondary border-l-2' : 'border-primary border-l-4 hover:border-primary-40' : ''"
                         class="relative mb-8 cursor-pointer rounded"
                         no-scroll-drag
                         @click="handleClick(application.id, $event)"
                         @dragstart="handleDragStart($event, application)"
                         @dragend="handleDragEnd"
                    >
                        <application
                            :id="'application-' + application.id"
                            :application="application"
                            :class="[canManageApplication(application) ? 'cursor-grab active:cursor-grabbing' : '']"
                            :color="application.rejected ? styles.colors['red-light'] : stage.color"
                            :draggable="canManageApplication(application)"
                            :job-has-selected="jobHasSelected"
                            :locale="locale"
                            :selected="selection.includes(application.id)"
                            :stage="stage"
                        />

                        <div class="absolute inset-y-0 right-0 flex items-center">
                            <checkbox v-model="selection"
                                      :value="application.id"
                                      class="mr-12"></checkbox>
                        </div>
                    </div>

                    <div v-if="stage.anonymised_count" class="text-center text-primary-80 pt-20">
                        <span v-if="applicationCount">+</span>
                        {{ $tc('applications.anonymised_applications', stage.anonymised_count, {count: stage.anonymised_count}) }}
                    </div>

                    <infinite-loading :identifier="sort.join() + showRejected"
                                      :slots="{complete: ' ', error: ' '}"
                                      @infinite="infiniteHandler">
                        <template #spinner>
                            <loading :color="stage.color" class="pt-48 mb-32"></loading>
                        </template>
                    </infinite-loading>
                </div>

            </div>
    </div>

        <div
            :class="[ isDragTarget ? 'opacity-100' : 'opacity-0' ]"
            class="absolute z-10 w-full h-full flex flex-col pointer-events-none trans-ease"
        >
            <div :class="[ scrollPadding ? 'rounded-tl' : 'rounded-t' ]"
                 class="mt-80 flex-grow flex items-center justify-center text-white relative overflow-hidden border border-primary-40">
                <div class="absolute inset-0 text-primary-80 opacity-25"></div>
                <svg-icon class="w-64 h-64 relative z-10" type="stage-drop"></svg-icon>
            </div>
        </div>

        <popup-modal-confirm
            v-if="hiringApplication && hasExternalPartner"
            :title="$t('applications.hire_subject', {subject: hiringApplication.first_name}) + '?'"
            :message="$t('applications.confirm_hire_one', {subject: hiringApplication.first_name})"
            :confirmText="$t('applications.hire_subject', {subject: hiringApplication.first_name})"
            :working="performingHireRequest"
            confirm-button="success"
            @confirm="hire"
            @close="cancelHire"
        >
            <hire-reason v-model="hireData" class="mt-16"/>
        </popup-modal-confirm>

    </div>
</template>

<script>
import Stage from '../models/Stage';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useNotificationsStore} from '../state/modules/notifications';
import {useApplicationsStore} from '../state/modules/applications';
import {useJobsStore} from '../state/modules/jobs';
import Checkbox from './forms/Checkbox.vue';
import Loading from './ui/Loading.vue';
import Application from '../models/Application';
import SvgIcon from './ui/SvgIcon.vue';
import ResizeObserver from 'resize-observer-polyfill';
import * as ApplicationService from '../services/ApplicationService';
import {ADD_NOTIFICATION} from '../state/mutation-types';
import Score from './ui/Score.vue';
import {normalizeJson} from '../../shared/helpers';
import InfiniteLoading from 'v3-infinite-loading/lib/v3-infinite-loading.es.js';
import styles from '../../../../../styles.config';
import StageApplication from './StageApplication.vue';
import Job from '../models/Job';
import ProgressIcon from './ui/ProgressIcon.vue';
import PopupModalConfirm from "./ui/PopupModalConfirm.vue";
import HireReason from "./HireReason.vue";

export default {
    components: {
        HireReason,
        PopupModalConfirm,
        'checkbox': Checkbox,
        'loading': Loading,
        'svg-icon': SvgIcon,
        'score': Score,
        'infinite-loading': InfiniteLoading,
        'application': StageApplication,
        'progress-icon': ProgressIcon,
    },
    props: {
        stage: {
            type: Stage,
            required: true,
        },
        job: Job,
        applications: Array,
        fetching: Boolean,
        jobHasSelected: Boolean,
        showRejected: Boolean,
        sort: Array,
    },
    data: function () {
        return {
            handleDropOriginals: [],
            handleDropRequests: [],
            handleDropCount: 0,
            scrollPadding: 0,
            isDragTarget: false,
            autoSelectNew: false,
            styles,
            next_page: 1,
            last_page: 1,
            hiringApplication: false,
            performingHireRequest: false,
            hireData: {
                reason: null,
                comment: "",
                comment_external: "",
            }
        };
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        ...mapState(useJobsStore, ['draggingApplications', 'selectedApplications', 'stagesWithAllApplicationsChecked']),
        applicationCount() {
            return this.showRejected ?
                this.stage.rejected_count :
                this.stage.applications_count - this.stage.rejected_count;
        },
        selection: {
            get() {
                return this.selectedApplications;
            },
            set(selection) {
                this.setSelectedApplications(selection);
            },
        },
        allApplicationsAreLoaded() {
            return this.applicationIds.length === this.applicationCount;
        },
        stageApplications() {
            let applications = this.applications.sort((a, b) => {

                let sort;

                switch (this.sort[0]) {
                    case 'score':

                        let scoreA = a.score == null ? (this.sortDesc ? -1 : 999) : a.score;
                        let scoreB = b.score == null ? (this.sortDesc ? -1 : 999) : b.score;

                        if (scoreA == scoreB) {
                            sort = a.createdMoment.isBefore(b.createdMoment) ? -1 : 1;
                        } else {
                            sort = scoreA > scoreB ? 1 : -1;
                        }
                        break;

                    case 'first_name':
                        if (a.first_name.toLowerCase() == b.first_name.toLowerCase()) {
                            sort = a.createdMoment.isBefore(b.createdMoment) ? -1 : 1;
                        } else {
                            sort = a.first_name.toLowerCase() > b.first_name.toLowerCase() ? 1 : -1;
                        }
                        break;

                    default:
                        sort = a.createdMoment.isBefore(b.createdMoment) ? -1 : 1;
                        break;
                }

                return this.sortDesc ? -sort : sort;
            });

            return this.showRejected
                ? applications.filter(application => application.rejected)
                : applications.filter(application => !application.rejected);
        },
        applicationIds() {
            return this.applications.map(({id}) => id);
        },
        sortDesc() {
            return this.sort[1] === 'desc';
        },
        canDrop() {
            return this.draggingApplications.length && !this.draggingApplications.map(application => application.stage_id).includes(this.stage.id);
        },
        hasSelectedItems() {
            return this.applicationIds.some(id => this.selection.includes(id)) || this.autoSelectNew;
        },
        hasUnselectedItems() {
            return this.applicationIds.some(id => !this.selection.includes(id))
                || (!this.autoSelectNew && !this.allApplicationsAreLoaded);
        },
        hasExternalPartner(){
            return !!this.hiringApplication.interim_candidate_id;
        },
        applicationIdsByRejectedState() {
            return this.stageApplications.map(({id}) => id);
        },
    },
    methods: {
        ...mapActions(useJobsStore, ['startDraggingApplications', 'stopDraggingApplications', 'setSelectedApplications',
            'setStagesWithAllApplicationsChecked']),
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        ...mapActions(useApplicationsStore, ['setSequence']),
        toggleSelectedApplications() {
            if (this.hasSelectedItems || this.autoSelectNew) {
                this.resetSelection();
            } else {
                this.addLoadedItemsToSelection();
                this.autoSelectNew = true;
            }
        },
        addLoadedItemsToSelection() {
            this.selection = [...new Set([...this.selection, ...this.applicationIdsByRejectedState])];
        },
        resetSelection() {
            this.selection = this.selection.filter(item => {
                return !this.applicationIds.includes(item);
            });
            this.autoSelectNew = false;
        },
        handleDragStart(e, application) {
            // check if multiple applications have been selected
            if (this.selection.length > 1) {
                let drawImage = document.createElement('div');
                drawImage.id = 'drag-image';
                drawImage.className = 'relative mt-40';
                // get all selected Applications and sort them by their created_at date
                let sortedSelection = JSON.parse(JSON.stringify(this.selection)).sort((a, b) => {
                    let applicationA = useRepo(Application).find(a);
                    let applicationB = useRepo(Application).find(b);
                    return applicationA.createdMoment.isBefore(applicationB.createdMoment) ? 1 : -1;
                });
                let zIndex = 1000;
                // reverse the selection so the first selected application is on top

                sortedSelection.forEach(application => {
                    // get the application from the application store
                    let applicationElement = document.getElementById('application-' + application)
                    let clone = applicationElement.cloneNode(true);
                    clone.classList.add('absolute', 'w-[300px]');
                    clone.style.zIndex = zIndex;
                    zIndex--;
                    // stack the applications with a negative offset
                    let offset = sortedSelection.indexOf(application) * 13;
                    clone.style.top = offset + 'px';
                    // make each application smaller than the previous one
                    let scale = 1 - (sortedSelection.indexOf(application) * 0.05);
                    clone.style.transform = 'scale(' + scale + ')';
                    drawImage.appendChild(clone);
                });
                document.body.appendChild(drawImage);
                e.dataTransfer.setDragImage(drawImage, 32, 32);
                this.startDraggingApplications(this.selection.map(id => useRepo(Application).find(id)));
            } else {
                this.startDraggingApplications([application]);
            }
        },
        handleDragEnd(e) {
            // remove the drag-image from the DOM
            let dragImage = document.getElementById('drag-image');
            if (dragImage) {
                dragImage.remove();
            }
            this.stopDraggingApplications();
        },
        handleDragEnter(e) {
            if (this.canDrop) {
                this.isDragTarget = true;
            }
        },
        handleDragLeave(e) {
            this.isDragTarget = false;
        },
        handleDrop(e) {
            if (this.canDrop) {

                this.isDragTarget = false;

                this.originals = [];
                this.requests = [];
                this.count = this.draggingApplications.length;

                this.updateStage(this.draggingApplications).then(() => {
                        this[ADD_NOTIFICATION]({
                            type: 'success',
                            message: this.$tc('jobs.moved_stage_success', this.handleDropCount, {
                                step: this.stage.name,
                            }),
                        });
                    this.handleDragEnd();
                })
            }
        },
        handleClick(applicationId, event) {
            if (event.target.getAttribute('xlink:href') === '#star') {
                return;
            }

            if (this.jobHasSelected) {
                let index = this.selection.indexOf(applicationId);

                if (index == -1) {
                    this.selection = [...new Set([...this.selection, applicationId])];
                } else {
                    this.selection.splice(index, 1);
                }

            } else {
                this.setSequence(this.applications.map(i => i.id));
                this.$router.push({name: 'application', params: {id: applicationId}});
            }
        },
        infiniteHandler(state) {
            let sort = (this.sortDesc ? '-' : '') + this.sort[0] + (this.sort[0] === 'created_at' ? '' : ',-created_at');

            return ApplicationService.getForStage(this.stage.id, sort, this.showRejected, this.next_page, 50)
                .then(result => {

                    this.setNextPage(result.meta.current_page + 1);
                    this.last_page = result.meta.last_page;

                    if (this.autoSelectNew) {
                        this.selection = [...new Set([
                            ...this.selection,
                            ...result.data.data.map(application => application.id),
                        ])];
                    }

                    return useRepo(Application).save(normalizeJson(result.data.data));

                }).then(inserts => {
                    if (this.next_page <= this.last_page) {
                        state.loaded();
                    } else {
                        state.complete();
                    }

                    this.setStageSelectionState((!this.applicationCount) || this.hasUnselectedItems);
                    this.updateScrollPadding();
                });
        },
        updateScrollPadding() {
            this.$nextTick(() => {
                let header = this.$refs.stageHeader;
                let container = this.$refs.stageContainer;

                let observer = new ResizeObserver((entries, observer) => {

                    this.scrollPadding = header.clientWidth - container.clientWidth;
                });

                if (container) {
                    observer.observe(container);
                }
            });
        },
        setStageSelectionState(value) {
            if (this.applicationCount) {
                if (value) {
                    this.setStagesWithAllApplicationsChecked(this.stagesWithAllApplicationsChecked.filter(
                        stageId => stageId !== this.stage.id),
                    );
                    this.autoSelectNew = false;
                } else {
                    this.setStagesWithAllApplicationsChecked([
                        ...new Set([...this.stagesWithAllApplicationsChecked, this.stage.id]),
                    ]);
                }
            }
        },

        canManageApplication(application) {
            return this.$can([
                'applications.manage',
                'applications.manage.' + application.id,
                'job-applications.manage.' + this.job?.id,
            ]);
        },
        setNextPage(number) {
            this.next_page = number;
        },
        updateStage(applications, comments = {}) {
            return new Promise(async (resolve, reject) => {
                let originals = this.handleDropOriginals;
                let requests = this.handleDropRequests;
                let applicationCount = applications.length;
                await applications.forEach(application => {
                    let prevStage = useRepo(Stage).find(application.stage_id);
                    if (this.stage.onboarding && application.interim_id && applicationCount === 1 && !this.hiringApplication) {
                        this.hiringApplication = application;
                    } else {
                        // Store a reference to the original application.
                        originals.push({...application});

                        // Update the application in our store to move it immediately.
                        useRepo(Application)
                            .whereId(application.id)
                            .update({stage_id: this.stage.id});

                        let original = originals.find(orig => orig.id === application.id);
                        requests.push(
                            ApplicationService.update(application.id, {
                                data: {
                                    attributes: {
                                        ...comments,
                                    },
                                    relationships: {
                                        stage: {
                                            data: {
                                                type: 'stages',
                                                id: this.stage.id,
                                            },
                                        },
                                    },
                                },
                            }).catch(error => {
                                // Move the application back to the original stage if there was an error
                                // in the request.
                                useRepo(Application).save(original);
                                reject(error);
                                throw (error);
                            }).then(result => {
                                return useRepo(Application).save(normalizeJson(result));
                            }).then(inserts => {
                                const stage = useRepo(Stage).find(this.stage.id);

                                const dataPrev = useRepo(Stage)
                                    .where('id', prevStage.id)
                                    .get()
                                    .map((s) => {
                                        const count = original.rejected ? s.rejected_count - 1 : s.rejected_count + 1;
                                        return {
                                            id: s.id,
                                            rejected_count: count
                                        }
                                    });
                                useRepo(Stage).save(dataPrev);

                                const dataCurrent = useRepo(Stage)
                                    .where('id', stage.id)
                                    .get()
                                    .map((s) => {
                                        return {
                                            id: s.id,
                                            applications_count: s.applications_count + 1
                                        }
                                    });
                                useRepo(Stage).save(dataCurrent);
                            })
                            .then((inserts) => {
                                if (this.hiringApplication) {
                                    this.cancelHire();
                                }
                            }).then(() => {
                                this.handleDropCount++;
                            })
                        );
                    }
                })
                Promise.all(requests).then(() => {
                    resolve();
                });
            })
        },
        hire() {
            this.performingHireRequest = true;

            this.updateStage([this.hiringApplication], {
                hire_reason: this.hireData.reason,
                hire_comment: this.hireData.comment,
                hire_comment_external: this.hireData.comment_external,
            });
        },
        cancelHire() {
            this.hiringApplication = false;
            this.movingStage = false;
            this.hireData = {
                reason: null,
                comment: "",
                comment_external: "",
            };
        },
    },
    watch: {
        hasUnselectedItems(value) {
            this.setStageSelectionState(value);
        },
    },
    mounted() {
        this.updateScrollPadding();
    },
};
</script>

<style scoped>
.gradient-bg {
    background: linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 1) 100%);
}

/*Reverse the background and use color given with stage*/
.gradient-bg-success {
    background: linear-gradient(0deg, rgb(246 246 244) 0%, rgb(246 246 244 / 40%) 40%, rgb(0 0 0 / 0%) 100%);
}
</style>
